/**
 * ReaderDetailPanelShell — Shared right tabbed sidebar wrapper for reader pages
 *
 * Handles: collapsed strip (w-10 + PanelRight toggle), expanded state with h-12 tab header,
 * icon tab buttons with optional badge dots, collapse button, scrollable content area,
 * optional bottom section, optional resize handle with localStorage persistence.
 */

import { useState, useCallback, useRef, type ReactNode } from 'react';
import { PanelRight, PanelRightClose } from 'lucide-react';

export interface TabDefinition {
  id: string;
  icon: ReactNode;
  title: string;
  badge?: boolean;
  badgeColor?: 'coral' | 'amber';
}

interface ReaderDetailPanelShellProps {
  tabs: TabDefinition[];
  activeTab: string;
  onTabChange: (tabId: string) => void;
  isCollapsed: boolean;
  onToggleCollapse: () => void;
  children: ReactNode;
  bottomSection?: ReactNode;
  width?: number;
  resizable?: boolean;
  minWidth?: number;
  maxWidth?: number;
  widthStorageKey?: string;
  className?: string;
  /** Icon to show in the collapsed strip (default: PanelRight) */
  collapsedIcon?: ReactNode;
  /** When true, sidebar is visible at lg+ instead of only xl+ */
  alwaysVisible?: boolean;
  /** When true, hide the default tab header bar (caller renders its own) */
  hideHeader?: boolean;
  /** When true, content area uses flex column layout instead of scroll — child manages its own scroll */
  fillContent?: boolean;
}

const BADGE_COLORS = {
  coral: 'bg-coral-500',
  amber: 'bg-amber-500',
} as const;

export function ReaderDetailPanelShell({
  tabs,
  activeTab,
  onTabChange,
  isCollapsed,
  onToggleCollapse,
  children,
  bottomSection,
  width: widthProp,
  resizable = false,
  minWidth = 240,
  maxWidth = 480,
  widthStorageKey,
  className = '',
  collapsedIcon,
  alwaysVisible = false,
  hideHeader = false,
  fillContent = false,
}: ReaderDetailPanelShellProps) {
  // Resizable width state (only used when resizable=true)
  const defaultWidth = widthProp ?? 288;
  const [resizableWidth, setResizableWidth] = useState(() => {
    if (!resizable || !widthStorageKey) return defaultWidth;
    try {
      const stored = localStorage.getItem(widthStorageKey);
      if (stored) {
        const parsed = parseInt(stored, 10);
        if (!isNaN(parsed) && parsed >= minWidth && parsed <= maxWidth) return parsed;
      }
    } catch { /* ignore */ }
    return defaultWidth;
  });

  const isResizing = useRef(false);
  const startX = useRef(0);
  const startWidth = useRef(defaultWidth);

  const handleResizeStart = useCallback((e: React.MouseEvent) => {
    if (!resizable) return;
    e.preventDefault();
    isResizing.current = true;
    startX.current = e.clientX;
    startWidth.current = resizableWidth;

    const handleMouseMove = (ev: MouseEvent) => {
      if (!isResizing.current) return;
      const delta = startX.current - ev.clientX;
      const newWidth = Math.min(maxWidth, Math.max(minWidth, startWidth.current + delta));
      setResizableWidth(newWidth);
    };

    const handleMouseUp = () => {
      isResizing.current = false;
      setResizableWidth(w => {
        if (widthStorageKey) {
          try { localStorage.setItem(widthStorageKey, String(w)); } catch { /* ignore */ }
        }
        return w;
      });
      window.document.removeEventListener('mousemove', handleMouseMove);
      window.document.removeEventListener('mouseup', handleMouseUp);
      window.document.body.style.cursor = '';
      window.document.body.style.userSelect = '';
    };

    window.document.addEventListener('mousemove', handleMouseMove);
    window.document.addEventListener('mouseup', handleMouseUp);
    window.document.body.style.cursor = 'col-resize';
    window.document.body.style.userSelect = 'none';
  }, [resizable, resizableWidth, minWidth, maxWidth, widthStorageKey]);

  const effectiveWidth = resizable ? resizableWidth : (widthProp ?? 288);

  const visibilityClass = alwaysVisible ? 'flex' : 'hidden xl:flex';

  // Collapsed state — just show toggle button
  if (isCollapsed) {
    return (
      <aside
        className={`w-10 flex-shrink-0 ${visibilityClass} flex-col bg-gradient-to-b from-white to-stone-50/50 border-l border-line/60 ${className}`}
      >
        <div className="p-2">
          <button
            onClick={onToggleCollapse}
            className="p-1.5 rounded-lg text-subtle hover:text-muted hover:bg-surface-subtle transition-colors"
            title="Expand sidebar"
          >
            {collapsedIcon || <PanelRight size={18} />}
          </button>
        </div>
      </aside>
    );
  }

  return (
    <aside
      className={`flex-shrink-0 ${visibilityClass} flex-col bg-gradient-to-b from-white to-stone-50/50 border-l border-line/60 relative ${className}`}
      style={{ width: `${effectiveWidth}px` }}
    >
      {/* Resize drag handle */}
      {resizable && (
        <div
          className="absolute left-0 top-0 bottom-0 w-1 cursor-col-resize z-10 hover:bg-coral-300 active:bg-coral-400 transition-colors"
          onMouseDown={handleResizeStart}
        />
      )}

      {/* Header with icon tabs and collapse button — h-12 aligns with other panels */}
      {!hideHeader && (
        <div className="h-12 flex items-center justify-between px-3 border-b border-line/60 bg-white/80 shrink-0">
          <div className="flex items-center gap-0.5">
            {tabs.map(tab => (
              <button
                key={tab.id}
                title={tab.title}
                onClick={() => onTabChange(tab.id)}
                className={`relative p-2 rounded-md transition-colors
                  ${activeTab === tab.id
                    ? 'bg-surface-subtle text-text'
                    : 'text-subtle hover:text-muted hover:bg-surface-muted'
                  }`}
              >
                {tab.icon}
                {tab.badge && (
                  <span
                    className={`absolute -top-0.5 -right-0.5 w-2 h-2 rounded-full ring-2 ring-white ${BADGE_COLORS[tab.badgeColor || 'coral']}`}
                  />
                )}
              </button>
            ))}
          </div>

          <button
            onClick={onToggleCollapse}
            className="p-1 rounded hover:bg-surface-subtle text-subtle hover:text-muted transition-colors"
            title="Collapse sidebar"
          >
            <PanelRightClose size={16} />
          </button>
        </div>
      )}

      {/* Content area — scrollable by default, flex column when fillContent is true */}
      <div className={fillContent ? "flex-1 flex flex-col min-h-0" : "flex-1 overflow-y-auto min-h-0"}>
        {children}
      </div>

      {/* Optional bottom section */}
      {bottomSection && (
        <div className="flex-shrink-0 max-h-[33%] flex flex-col">
          {bottomSection}
        </div>
      )}
    </aside>
  );
}

export default ReaderDetailPanelShell;
